import { FormApi, FormTemplate } from '@icure/cardinal-sdk'
import { FormTemplateFilters } from '@icure/cardinal-sdk/filters.mjs'
import { Form } from '@icure/form/components/model'
import YAML from 'yaml'
import { PREVENTI_FORM_TEMPLATE_GUID, PREVENTI_FORM_TEMPLATE_SPECIALTY_CODE } from '../../constants'

function parseTimestampFromName(name: string | undefined): number {
  if (!name) return 0
  const match = name.match(/Preventi\s+(.+)/)
  if (!match) return 0
  const date = new Date(match[1])
  return isNaN(date.getTime()) ? 0 : date.getTime()
}

// Fetches the templates raw, one by one: the server rejects bulk and non-raw fetches (HTTP 400) of any template
// whose layout it cannot parse, which is the case for the current Preventi templates.
export async function getRawFormTemplates(formApi: FormApi, ids: string[]): Promise<FormTemplate[]> {
  return Promise.all(ids.map((id) => formApi.getFormTemplate(id, { raw: true })))
}

export async function resolveFormTemplate(formApi: FormApi, formTemplateId: string | undefined): Promise<Form> {
  // Try specific version first
  if (formTemplateId) {
    try {
      const template = await formApi.getFormTemplate(formTemplateId, { raw: true })
      if (template.rawTemplateLayout) {
        return Form.parse(YAML.parse(new TextDecoder().decode(template.rawTemplateLayout)))
      }
    } catch {
      // Fall through to latest
    }
  }

  // Fall back to latest by specialty code + guid
  const ids = await formApi.matchFormTemplateBy(FormTemplateFilters.bySpecialtyCode(PREVENTI_FORM_TEMPLATE_SPECIALTY_CODE))
  if (!ids.length) {
    throw new Error('No form templates found. Please upload a form template first.')
  }
  const templates = await getRawFormTemplates(formApi, ids)
  const filtered = templates.filter((t) => t.guid === PREVENTI_FORM_TEMPLATE_GUID)
  if (!filtered.length) {
    throw new Error('No Preventi form templates found. Please upload a form template first.')
  }
  const sorted = [...filtered].sort((a, b) => parseTimestampFromName(b.name) - parseTimestampFromName(a.name))
  const latest = sorted[0]
  const latestTemplate = await formApi.getFormTemplate(latest.id, { raw: true })
  if (!latestTemplate.rawTemplateLayout) {
    throw new Error('Latest form template has no attachment.')
  }
  return Form.parse(YAML.parse(new TextDecoder().decode(latestTemplate.rawTemplateLayout)))
}

export async function resolveLatestTemplateId(formApi: FormApi): Promise<string | undefined> {
  const ids = await formApi.matchFormTemplateBy(FormTemplateFilters.bySpecialtyCode(PREVENTI_FORM_TEMPLATE_SPECIALTY_CODE))
  if (!ids.length) return undefined
  const templates = await getRawFormTemplates(formApi, ids)
  const filtered = templates.filter((t) => t.guid === PREVENTI_FORM_TEMPLATE_GUID)
  if (!filtered.length) return undefined
  const sorted = [...filtered].sort((a, b) => parseTimestampFromName(b.name) - parseTimestampFromName(a.name))
  return sorted[0]?.id
}
